/**
 * A small, page-break-aware layout engine on top of jsPDF.
 *
 * jsPDF draws text at coordinates and knows nothing about flow, wrapping or
 * pagination. `PdfLayout` adds a top-down cursor, mixed-style inline wrapping
 * (bold/italic/code/links inside one paragraph), and block renderers for the
 * markdown subset chat answers actually use.
 *
 * Nothing here is chat-specific — `chat-transcript.ts` composes it.
 */

import { jsPDF } from 'jspdf';
import { PdfBlock, PdfTableCell, PdfTextRun } from './types';
import { PDF_COLOR, PDF_FONT, PDF_MARGIN, PDF_SPACE, Rgb, headingSize } from './theme';
import { sanitizeForPdf } from './text';

interface LayoutToken {
  text: string;
  width: number;
  run: PdfTextRun;
  /** Whitespace tokens are dropped when they fall at the end of a line. */
  space: boolean;
}

interface LayoutLine {
  tokens: LayoutToken[];
  width: number;
}

export interface TextOptions {
  size?: number;
  lineHeight?: number;
  color?: Rgb;
  bold?: boolean;
  italic?: boolean;
  /** Extra indent from the content left edge. */
  indent?: number;
  /** Width override; defaults to the content width minus `indent`. */
  width?: number;
  align?: 'left' | 'right' | 'center';
}

export interface ImagePlacement {
  dataUrl: string;
  format: string;
  width: number;
  height: number;
}

const FALLBACK_FONT = 'helvetica';
const CODE_FONT = 'courier';

const fontStyle = (bold?: boolean, italic?: boolean): string => {
  if (bold && italic) return 'bolditalic';
  if (bold) return 'bold';
  if (italic) return 'italic';
  return 'normal';
};

export class PdfLayout {
  readonly doc: jsPDF;
  readonly pageWidth: number;
  readonly pageHeight: number;
  readonly contentWidth: number;

  private cursorY: number;
  private pageBreakListeners: (() => void)[] = [];

  constructor(doc?: jsPDF) {
    this.doc = doc || new jsPDF({ unit: 'pt', format: 'a4', orientation: 'portrait' });
    this.pageWidth = this.doc.internal.pageSize.getWidth();
    this.pageHeight = this.doc.internal.pageSize.getHeight();
    this.contentWidth = this.pageWidth - PDF_MARGIN.left - PDF_MARGIN.right;
    this.cursorY = PDF_MARGIN.top;
  }

  // ─── Cursor + pagination ───────────────────────────────────────────────────

  get y(): number {
    return this.cursorY;
  }

  set y(value: number) {
    this.cursorY = value;
  }

  get contentTop(): number {
    return PDF_MARGIN.top;
  }

  get contentBottom(): number {
    return this.pageHeight - PDF_MARGIN.bottom;
  }

  get left(): number {
    return PDF_MARGIN.left;
  }

  get right(): number {
    return this.pageWidth - PDF_MARGIN.right;
  }

  space(amount: number): void {
    this.cursorY += amount;
  }

  addPage(): void {
    // Listeners close out their decorations on the page being left, so they run
    // before jsPDF switches the current page.
    this.pageBreakListeners.forEach((listener) => listener());
    this.doc.addPage();
    this.cursorY = PDF_MARGIN.top;
  }

  /** Breaks to a new page unless `height` still fits below the cursor. */
  ensureSpace(height: number): void {
    if (this.cursorY + height <= this.contentBottom) return;
    this.addPage();
  }

  /**
   * Runs `render` while drawing a vertical accent rule to the left of whatever
   * it produces, re-starting the rule on every page the content spills onto.
   */
  withLeftRule(color: Rgb, offset: number, render: () => void): void {
    let segmentStart = this.cursorY;

    const drawSegment = (endY: number) => {
      if (endY - segmentStart < 1) return;
      this.setDraw(color);
      this.doc.setLineWidth(1.6);
      this.doc.line(this.left + offset, segmentStart, this.left + offset, endY);
      this.doc.setLineWidth(0.5);
    };

    const listener = () => {
      drawSegment(this.contentBottom);
      segmentStart = this.contentTop;
    };

    this.pageBreakListeners.push(listener);
    try {
      render();
    } finally {
      this.pageBreakListeners = this.pageBreakListeners.filter((item) => item !== listener);
    }
    drawSegment(this.cursorY);
  }

  // ─── Primitives ────────────────────────────────────────────────────────────

  setFill(color: Rgb): void {
    this.doc.setFillColor(color[0], color[1], color[2]);
  }

  setDraw(color: Rgb): void {
    this.doc.setDrawColor(color[0], color[1], color[2]);
  }

  setInk(color: Rgb): void {
    this.doc.setTextColor(color[0], color[1], color[2]);
  }

  rule(color: Rgb = PDF_COLOR.rule, indent = 0): void {
    this.setDraw(color);
    this.doc.setLineWidth(0.5);
    this.doc.line(this.left + indent, this.cursorY, this.right, this.cursorY);
  }

  /** Single line of text, no wrapping. Returns the height consumed. */
  line(text: string, options: TextOptions = {}): number {
    const size = options.size || PDF_FONT.body;
    const lineHeight = size * (options.lineHeight || PDF_FONT.lineHeight);
    const indent = options.indent || 0;
    this.ensureSpace(lineHeight);

    this.doc.setFont(FALLBACK_FONT, fontStyle(options.bold, options.italic));
    this.doc.setFontSize(size);
    this.setInk(options.color || PDF_COLOR.body);

    const clean = sanitizeForPdf(text);
    let x = this.left + indent;
    if (options.align === 'right') x = this.right;
    else if (options.align === 'center') x = this.left + this.contentWidth / 2;

    this.doc.text(clean, x, this.cursorY, {
      baseline: 'top',
      align: options.align || 'left',
    });
    this.cursorY += lineHeight;
    return lineHeight;
  }

  // ─── Inline layout ─────────────────────────────────────────────────────────

  private applyRunFont(run: PdfTextRun, baseSize: number): number {
    const size = this.runSize(run, baseSize);
    this.doc.setFont(run.code ? CODE_FONT : FALLBACK_FONT, fontStyle(run.bold, run.italic));
    this.doc.setFontSize(size);
    return size;
  }

  private runSize(run: PdfTextRun, baseSize: number): number {
    if (run.superscript) return baseSize * 0.68;
    if (run.code) return baseSize * 0.92;
    return baseSize;
  }

  /** Splits a token that cannot fit on its own line (long URLs, hashes). */
  private splitOversizedToken(token: string, maxWidth: number): string[] {
    const parts: string[] = [];
    let current = '';

    Array.from(token).forEach((char) => {
      const candidate = current + char;
      if (current && this.doc.getTextWidth(candidate) > maxWidth) {
        parts.push(current);
        current = char;
      } else {
        current = candidate;
      }
    });

    if (current) parts.push(current);
    return parts;
  }

  /** Greedy line breaker across runs with mixed fonts. */
  layoutRuns(runs: PdfTextRun[], width: number, baseSize: number): LayoutLine[] {
    const lines: LayoutLine[] = [];
    let line: LayoutLine = { tokens: [], width: 0 };

    const flush = () => {
      while (line.tokens.length && line.tokens[line.tokens.length - 1].space) {
        line.width -= line.tokens[line.tokens.length - 1].width;
        line.tokens.pop();
      }
      lines.push(line);
      line = { tokens: [], width: 0 };
    };

    runs.forEach((run) => {
      if (run.lineBreak) {
        flush();
        return;
      }

      this.applyRunFont(run, baseSize);
      const tokens = run.text.match(/\S+|\s+/g) || [];

      tokens.forEach((raw) => {
        const isSpace = /^\s+$/.test(raw);
        const text = isSpace ? ' ' : raw;
        let tokenWidth = this.doc.getTextWidth(text);

        // Leading whitespace on a fresh line is dropped.
        if (isSpace && !line.tokens.length) return;

        if (!isSpace && tokenWidth > width && line.width === 0) {
          this.splitOversizedToken(text, width).forEach((piece, index, all) => {
            const pieceWidth = this.doc.getTextWidth(piece);
            line.tokens.push({ text: piece, width: pieceWidth, run, space: false });
            line.width += pieceWidth;
            if (index < all.length - 1) flush();
          });
          return;
        }

        if (line.width + tokenWidth > width && line.tokens.length) {
          flush();
          if (isSpace) return;
          this.applyRunFont(run, baseSize);
          tokenWidth = this.doc.getTextWidth(text);
        }

        line.tokens.push({ text, width: tokenWidth, run, space: isSpace });
        line.width += tokenWidth;
      });
    });

    flush();
    return lines.filter((item, index) => item.tokens.length > 0 || index < lines.length - 1);
  }

  /** Height a run sequence would occupy at the given width. */
  measureRuns(
    runs: PdfTextRun[],
    width: number,
    baseSize: number,
    lineHeightFactor?: number
  ): number {
    const lines = this.layoutRuns(runs, width, baseSize);
    return lines.length * baseSize * (lineHeightFactor || PDF_FONT.lineHeight);
  }

  private drawLine(
    line: LayoutLine,
    x: number,
    baseSize: number,
    lineHeight: number,
    color: Rgb
  ): void {
    // Consecutive tokens from one run are drawn as a single text operator, so
    // copied text keeps its spacing instead of arriving word by word.
    const spans: { run: PdfTextRun; text: string; width: number }[] = [];
    line.tokens.forEach((token) => {
      const last = spans[spans.length - 1];
      if (last && last.run === token.run) {
        last.text += token.text;
        last.width += token.width;
        return;
      }
      spans.push({ run: token.run, text: token.text, width: token.width });
    });

    let cursorX = x;

    spans.forEach((span) => {
      const size = this.applyRunFont(span.run, baseSize);
      const run = span.run;
      const top = run.superscript ? this.cursorY : this.cursorY + (baseSize - size) * 0.5;
      const visible = span.text.trim().length > 0;

      if (run.code && !run.superscript && visible) {
        this.setFill(PDF_COLOR.codeBg);
        this.doc.rect(cursorX - 1, this.cursorY - 1, span.width + 2, baseSize * 1.18, 'F');
      }

      this.setInk(run.href ? PDF_COLOR.link : run.code ? PDF_COLOR.codeInk : color);
      this.doc.text(span.text, cursorX, top, { baseline: 'top' });

      if (run.href && visible) {
        const underlineY = this.cursorY + baseSize * 0.98;
        this.setDraw(PDF_COLOR.link);
        this.doc.setLineWidth(0.4);
        this.doc.line(cursorX, underlineY, cursorX + span.width, underlineY);
        this.doc.link(cursorX, this.cursorY, span.width, lineHeight, { url: run.href });
      }

      if (run.strike && visible) {
        const strikeY = this.cursorY + baseSize * 0.45;
        this.setDraw(color);
        this.doc.setLineWidth(0.5);
        this.doc.line(cursorX, strikeY, cursorX + span.width, strikeY);
      }

      cursorX += span.width;
    });
  }

  /** Wrapped, mixed-style paragraph. Breaks pages line by line. */
  paragraph(runs: PdfTextRun[], options: TextOptions = {}): void {
    const size = options.size || PDF_FONT.body;
    const lineHeight = size * (options.lineHeight || PDF_FONT.lineHeight);
    const indent = options.indent || 0;
    const width = options.width || this.contentWidth - indent;
    const color = options.color || PDF_COLOR.body;
    const lines = this.layoutRuns(runs, width, size);

    lines.forEach((line) => {
      this.ensureSpace(lineHeight);
      this.drawLine(line, this.left + indent, size, lineHeight, color);
      this.cursorY += lineHeight;
    });
  }

  // ─── Blocks ────────────────────────────────────────────────────────────────

  renderBlocks(blocks: PdfBlock[], indent = 0): void {
    blocks.forEach((block, index) => {
      switch (block.type) {
        case 'heading': {
          const size = headingSize(block.depth);
          if (index > 0) this.space(PDF_SPACE.heading);
          // Keep a heading with at least the first line of what follows.
          this.ensureSpace(size * 2.6);
          this.paragraph(block.runs, {
            size,
            indent,
            color: PDF_COLOR.ink,
            lineHeight: 1.3,
          });
          this.space(PDF_SPACE.headingAfter);
          break;
        }

        case 'paragraph':
          this.paragraph(block.runs, { indent });
          this.space(PDF_SPACE.paragraph);
          break;

        case 'list':
          this.renderList(block, indent);
          this.space(PDF_SPACE.paragraph);
          break;

        case 'code':
          this.renderCode(block.value, indent);
          this.space(PDF_SPACE.block);
          break;

        case 'blockquote':
          this.withLeftRule(PDF_COLOR.rule, indent + 2, () => {
            this.renderBlocks(block.blocks, indent + PDF_SPACE.quoteIndent);
          });
          break;

        case 'table':
          this.renderTable(block.header, block.rows, indent);
          this.space(PDF_SPACE.block);
          break;

        case 'thematicBreak':
          this.space(PDF_SPACE.ruleGap);
          this.ensureSpace(PDF_SPACE.ruleGap);
          this.rule(PDF_COLOR.rule, indent);
          this.space(PDF_SPACE.ruleGap);
          break;

        default:
          break;
      }
    });
  }

  private renderList(block: Extract<PdfBlock, { type: 'list' }>, indent: number, depth = 0): void {
    const markerWidth = PDF_SPACE.listIndent;
    const bullets = ['•', '-', '·'];

    block.items.forEach((item, index) => {
      const marker = block.ordered
        ? `${block.start + index}.`
        : item.checked === true
          ? '[x]'
          : item.checked === false
            ? '[ ]'
            : bullets[Math.min(depth, bullets.length - 1)];

      this.ensureSpace(PDF_FONT.body * PDF_FONT.lineHeight);
      const markerY = this.cursorY;

      // Draw the item body first so the marker aligns with its first line even
      // when the body starts with a nested block.
      const bodyIndent = indent + markerWidth;
      const startPage = this.doc.getNumberOfPages();

      this.renderListItemBody(item.blocks, bodyIndent, depth);

      // The marker belongs on the page the item started on.
      const endPage = this.doc.getNumberOfPages();
      if (endPage !== startPage) this.doc.setPage(startPage);
      this.doc.setFont(FALLBACK_FONT, 'normal');
      this.doc.setFontSize(PDF_FONT.body);
      this.setInk(PDF_COLOR.muted);
      this.doc.text(marker, this.left + indent + markerWidth - 6, markerY, {
        baseline: 'top',
        align: 'right',
      });
      if (endPage !== startPage) this.doc.setPage(endPage);

      this.space(PDF_SPACE.listItem);
    });
  }

  private renderListItemBody(blocks: PdfBlock[], indent: number, depth: number): void {
    blocks.forEach((block, index) => {
      if (block.type === 'list') {
        this.renderList(block, indent, depth + 1);
        return;
      }
      if (block.type === 'paragraph') {
        this.paragraph(block.runs, { indent });
        if (index < blocks.length - 1) this.space(PDF_SPACE.paragraph);
        return;
      }
      this.renderBlocks([block], indent);
    });
  }

  private renderCode(value: string, indent: number): void {
    const size = PDF_FONT.code;
    const lineHeight = size * PDF_FONT.codeLineHeight;
    const width = this.contentWidth - indent;
    const innerWidth = width - PDF_SPACE.codePadX * 2;

    this.doc.setFont(CODE_FONT, 'normal');
    this.doc.setFontSize(size);

    const lines: string[] = [];
    value.split('\n').forEach((raw) => {
      if (!raw) {
        lines.push('');
        return;
      }
      if (this.doc.getTextWidth(raw) <= innerWidth) {
        lines.push(raw);
        return;
      }
      lines.push(...this.splitOversizedToken(raw, innerWidth));
    });

    // Pad the block so the tinted background has breathing room top and bottom.
    const drawBand = (height: number) => {
      this.setFill(PDF_COLOR.codeBg);
      this.doc.rect(this.left + indent, this.cursorY, width, height, 'F');
    };

    this.ensureSpace(lineHeight + PDF_SPACE.codePadY * 2);
    drawBand(PDF_SPACE.codePadY);
    this.cursorY += PDF_SPACE.codePadY;

    lines.forEach((line) => {
      if (this.cursorY + lineHeight > this.contentBottom) {
        drawBand(this.contentBottom - this.cursorY);
        this.addPage();
        drawBand(PDF_SPACE.codePadY);
        this.cursorY += PDF_SPACE.codePadY;
      }
      drawBand(lineHeight);
      this.doc.setFont(CODE_FONT, 'normal');
      this.doc.setFontSize(size);
      this.setInk(PDF_COLOR.codeInk);
      this.doc.text(line, this.left + indent + PDF_SPACE.codePadX, this.cursorY + 1, {
        baseline: 'top',
      });
      this.cursorY += lineHeight;
    });

    drawBand(PDF_SPACE.codePadY);
    this.cursorY += PDF_SPACE.codePadY;
  }

  private renderTable(header: PdfTableCell[], rows: PdfTableCell[][], indent: number): void {
    const columnCount = Math.max(header.length, ...rows.map((row) => row.length), 1);
    const available = this.contentWidth - indent;
    const size = PDF_FONT.small;
    const lineHeight = size * PDF_FONT.lineHeight;

    const columns = this.computeColumnWidths(header, rows, columnCount, available, size);

    const rowHeight = (cells: PdfTableCell[], bold: boolean): number => {
      let maxLines = 1;
      columns.forEach((columnWidth, columnIndex) => {
        const cell = cells[columnIndex];
        if (!cell) return;
        const runs = bold ? cell.runs.map((run) => ({ ...run, bold: true })) : cell.runs;
        const lines = this.layoutRuns(runs, columnWidth - PDF_SPACE.cellPadX * 2, size);
        maxLines = Math.max(maxLines, lines.length);
      });
      return maxLines * lineHeight + PDF_SPACE.cellPadY * 2;
    };

    const drawRow = (cells: PdfTableCell[], bold: boolean, fill: boolean): void => {
      const height = rowHeight(cells, bold);
      this.ensureSpace(height);
      const top = this.cursorY;

      if (fill) {
        this.setFill(PDF_COLOR.tableHeadBg);
        this.doc.rect(this.left + indent, top, available, height, 'F');
      }

      let x = this.left + indent;
      columns.forEach((columnWidth, columnIndex) => {
        const cell = cells[columnIndex];
        if (cell) {
          const runs = bold ? cell.runs.map((run) => ({ ...run, bold: true })) : cell.runs;
          const lines = this.layoutRuns(runs, columnWidth - PDF_SPACE.cellPadX * 2, size);
          const savedY = this.cursorY;
          this.cursorY = top + PDF_SPACE.cellPadY;
          lines.forEach((line) => {
            this.drawLine(
              line,
              x + PDF_SPACE.cellPadX,
              size,
              lineHeight,
              bold ? PDF_COLOR.ink : PDF_COLOR.body
            );
            this.cursorY += lineHeight;
          });
          this.cursorY = savedY;
        }
        x += columnWidth;
      });

      this.cursorY = top + height;
      this.setDraw(PDF_COLOR.rule);
      this.doc.setLineWidth(0.5);
      this.doc.line(this.left + indent, this.cursorY, this.left + indent + available, this.cursorY);
    };

    const headerHeight = rowHeight(header, true);
    this.ensureSpace(headerHeight + lineHeight * 2);
    if (header.length) drawRow(header, true, true);

    rows.forEach((row) => {
      const height = rowHeight(row, false);
      // Repeat the header whenever the table continues on a new page.
      if (this.cursorY + height > this.contentBottom) {
        this.addPage();
        if (header.length) drawRow(header, true, true);
      }
      drawRow(row, false, false);
    });
  }

  private computeColumnWidths(
    header: PdfTableCell[],
    rows: PdfTableCell[][],
    columnCount: number,
    available: number,
    size: number
  ): number[] {
    const minWidth = 46;
    const natural: number[] = new Array(columnCount).fill(0);

    const measureCell = (cell: PdfTableCell | undefined, bold: boolean): number => {
      if (!cell) return 0;
      let width = 0;
      cell.runs.forEach((run) => {
        if (run.lineBreak) return;
        this.doc.setFont(
          run.code ? CODE_FONT : FALLBACK_FONT,
          fontStyle(bold || run.bold, run.italic)
        );
        this.doc.setFontSize(this.runSize(run, size));
        width += this.doc.getTextWidth(run.text);
      });
      return width + PDF_SPACE.cellPadX * 2;
    };

    for (let i = 0; i < columnCount; i += 1) {
      natural[i] = measureCell(header[i], true);
    }
    rows.forEach((row) => {
      for (let i = 0; i < columnCount; i += 1) {
        natural[i] = Math.max(natural[i], measureCell(row[i], false));
      }
    });

    const total = natural.reduce((sum, width) => sum + width, 0);
    if (total <= available) {
      // Distribute the slack so the table fills the text column.
      const extra = (available - total) / columnCount;
      return natural.map((width) => width + extra);
    }

    // Shrink wide columns toward the minimum, keeping narrow ones intact.
    const scaled = natural.map((width) => Math.max(minWidth, (width / total) * available));
    const scaledTotal = scaled.reduce((sum, width) => sum + width, 0);
    if (scaledTotal <= available) return scaled;
    return scaled.map((width) => (width / scaledTotal) * available);
  }

  // ─── Images ────────────────────────────────────────────────────────────────

  image(placement: ImagePlacement, maxHeight = 200, indent = 0): void {
    const maxWidth = this.contentWidth - indent;
    const ratio = placement.height / placement.width || 1;
    let width = Math.min(placement.width, maxWidth);
    let height = width * ratio;

    if (height > maxHeight) {
      height = maxHeight;
      width = height / ratio;
    }

    const usableHeight = this.contentBottom - this.contentTop;
    if (height > usableHeight) {
      height = usableHeight;
      width = height / ratio;
    }

    this.ensureSpace(height);
    try {
      this.doc.addImage(
        placement.dataUrl,
        placement.format,
        this.left + indent,
        this.cursorY,
        width,
        height
      );
      this.cursorY += height;
    } catch (error) {
      // A malformed data URL should not abort the whole export.
      this.line('[image could not be embedded]', {
        size: PDF_FONT.small,
        color: PDF_COLOR.faint,
        italic: true,
        indent,
      });
    }
  }

  /** Small rounded chip, used for non-image attachments. */
  chip(text: string, indent = 0): void {
    const size = PDF_FONT.tiny;
    const height = size * 1.9;
    this.ensureSpace(height + 2);

    this.doc.setFont(FALLBACK_FONT, 'normal');
    this.doc.setFontSize(size);
    const clean = sanitizeForPdf(text);
    const width = Math.min(this.doc.getTextWidth(clean) + 14, this.contentWidth - indent);

    this.setFill(PDF_COLOR.chipBg);
    this.setDraw(PDF_COLOR.rule);
    this.doc.setLineWidth(0.5);
    this.doc.roundedRect(this.left + indent, this.cursorY, width, height, 3, 3, 'FD');
    this.setInk(PDF_COLOR.muted);
    this.doc.text(clean, this.left + indent + 7, this.cursorY + height / 2, {
      baseline: 'middle',
    });

    this.cursorY += height + 3;
  }

  // ─── Footer ────────────────────────────────────────────────────────────────

  /** Post-pass: stamps `title` and `Page n of m` onto every page. */
  stampFooters(title: string): void {
    const total = this.doc.getNumberOfPages();
    const footerY = this.pageHeight - PDF_MARGIN.bottom + 24;
    const clean = sanitizeForPdf(title);

    for (let page = 1; page <= total; page += 1) {
      this.doc.setPage(page);
      this.doc.setFont(FALLBACK_FONT, 'normal');
      this.doc.setFontSize(PDF_FONT.tiny);
      this.setInk(PDF_COLOR.faint);

      this.setDraw(PDF_COLOR.rule);
      this.doc.setLineWidth(0.5);
      this.doc.line(this.left, footerY - 10, this.right, footerY - 10);

      this.doc.setFontSize(PDF_FONT.tiny);
      const maxTitleWidth = this.contentWidth - 90;
      let footerTitle = clean;
      while (footerTitle && this.doc.getTextWidth(footerTitle) > maxTitleWidth) {
        footerTitle = footerTitle.slice(0, -2);
      }
      if (footerTitle !== clean && footerTitle) footerTitle += '…';

      this.doc.text(footerTitle, this.left, footerY, { baseline: 'top' });
      this.doc.text(`Page ${page} of ${total}`, this.right, footerY, {
        baseline: 'top',
        align: 'right',
      });
    }

    this.doc.setPage(total);
  }
}
